import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Layers, HelpCircle, ListOrdered, Sparkles } from "lucide-react";
import api from "../lib/api";

const ICON_OPTIONS = ["truck", "sparkles", "tag", "headset", "package", "award"];

const BLANK_SLIDE = () => ({
    key: `slide-${Date.now()}`,
    active: true,
    eyebrow: "// New slide",
    headline_top: "Headline",
    headline_accent: "Accent",
    sub: "Short, punchy supporting line.",
    cta_label: "Shop now",
    cta_href: "/shop",
    secondary_cta_label: "Lab Standards",
    secondary_cta_href: "/lab-standards",
    badge: null,
    image: null,
});

export const AdminHomepagePanel = ({ adminToken }) => {
    const [slidesDoc, setSlidesDoc] = useState(null);
    const [feed, setFeed] = useState(null);
    const [sections, setSections] = useState(null);
    const [saving, setSaving] = useState(false);

    const headers = { "x-admin-token": adminToken };

    useEffect(() => {
        api.get("/admin/home/slides", { headers }).then((r) => setSlidesDoc(r.data)).catch(() => {});
        api.get("/admin/home/feed", { headers }).then((r) => setFeed(r.data)).catch(() => {});
        api.get("/admin/home/sections", { headers }).then((r) => setSections(r.data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveSections = async () => {
        setSaving(true);
        try {
            const payload = {
                order: sections.order,
                visible: sections.visible,
                how_it_works: sections.how_it_works,
                why_choose: sections.why_choose,
                faq: sections.faq,
            };
            const { data } = await api.put("/admin/home/sections", payload, { headers });
            setSections(data);
            toast.success("Homepage sections updated");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to save sections");
        } finally {
            setSaving(false);
        }
    };

    const saveSlides = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/home/slides", { slides: slidesDoc.slides }, { headers });
            setSlidesDoc(data);
            toast.success("Hero slides updated");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to save slides");
        } finally {
            setSaving(false);
        }
    };

    const saveFeed = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/home/feed", feed, { headers });
            setFeed(data);
            toast.success("Live feed updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save feed");
        } finally {
            setSaving(false);
        }
    };

    /* ── Slide helpers ── */
    const updateSlide = (i, patch) => {
        const next = [...slidesDoc.slides];
        next[i] = { ...next[i], ...patch };
        setSlidesDoc({ ...slidesDoc, slides: next });
    };
    const moveSlide = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= slidesDoc.slides.length) return;
        const next = [...slidesDoc.slides];
        [next[i], next[j]] = [next[j], next[i]];
        setSlidesDoc({ ...slidesDoc, slides: next });
    };
    const removeSlide = (i) => {
        if (slidesDoc.slides.length <= 1) {
            toast.error("At least one slide is required");
            return;
        }
        setSlidesDoc({ ...slidesDoc, slides: slidesDoc.slides.filter((_, idx) => idx !== i) });
    };
    const addSlide = () => {
        if (slidesDoc.slides.length >= 8) {
            toast.error("Maximum 8 slides");
            return;
        }
        setSlidesDoc({ ...slidesDoc, slides: [...slidesDoc.slides, BLANK_SLIDE()] });
    };

    /* ── Feed helpers ── */
    const addMessage = () => {
        setFeed({ ...feed, messages: [...(feed?.messages || []), { icon: "sparkles", text: "", active: true }] });
    };
    const updateMessage = (i, patch) => {
        const next = [...feed.messages];
        next[i] = { ...next[i], ...patch };
        setFeed({ ...feed, messages: next });
    };
    const removeMessage = (i) => {
        setFeed({ ...feed, messages: feed.messages.filter((_, idx) => idx !== i) });
    };

    if (!slidesDoc || !feed) {
        return <div className="text-[var(--text-muted)] text-sm">Loading homepage settings...</div>;
    }

    const inputCls = "w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)] transition-colors";
    const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1";

    return (
        <div className="space-y-10" data-testid="admin-homepage-panel">
            {/* Hero slides editor */}
            <section className="glass-elevated rounded-2xl p-6 md:p-8 border border-white/5">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div>
                        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <Layers size={16} className="text-[var(--gold)]" />
                            Hero Carousel Slides
                        </h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                            Auto-rotating slides at the top of the homepage. Drag-handle ordering, per-slide active toggle.
                        </p>
                    </div>
                    <button onClick={addSlide} className="btn-ghost" data-testid="slide-add-btn">
                        <Plus size={14} /> Add slide
                    </button>
                </div>

                <div className="space-y-4" data-testid="slides-list">
                    {slidesDoc.slides.map((s, i) => (
                        <div
                            key={`${s.key}-${i}`}
                            className={`relative rounded-xl p-4 md:p-5 border transition-colors ${s.active ? "border-[rgba(233,188,11,0.25)] bg-black/30" : "border-white/5 bg-black/20 opacity-70"}`}
                            data-testid={`slide-row-${i}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex flex-col items-center gap-1 pt-2">
                                    <GripVertical size={14} className="text-[var(--text-muted)]" />
                                    <button
                                        onClick={() => moveSlide(i, -1)}
                                        disabled={i === 0}
                                        className="p-1 rounded text-[var(--text-muted)] hover:text-white disabled:opacity-20"
                                        aria-label="Move up"
                                        data-testid={`slide-up-${i}`}
                                    >
                                        <ChevronUp size={12} />
                                    </button>
                                    <button
                                        onClick={() => moveSlide(i, 1)}
                                        disabled={i === slidesDoc.slides.length - 1}
                                        className="p-1 rounded text-[var(--text-muted)] hover:text-white disabled:opacity-20"
                                        aria-label="Move down"
                                        data-testid={`slide-down-${i}`}
                                    >
                                        <ChevronDown size={12} />
                                    </button>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--gold)]">
                                            <span>Slide #{i + 1}</span>
                                            <span className="text-[var(--text-muted)]">·</span>
                                            <span className="text-[var(--text-muted)]">{s.key}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => updateSlide(i, { active: !s.active })}
                                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${s.active ? "border-[var(--tiffany)] text-[var(--tiffany)] bg-[var(--tiffany-soft)]" : "border-white/10 text-[var(--text-muted)]"}`}
                                                data-testid={`slide-toggle-${i}`}
                                            >
                                                {s.active ? <Eye size={11} /> : <EyeOff size={11} />}
                                                {s.active ? "Visible" : "Hidden"}
                                            </button>
                                            <button
                                                onClick={() => removeSlide(i)}
                                                className="p-1.5 rounded border border-white/10 text-[var(--text-muted)] hover:text-red-400 hover:border-red-400/40"
                                                aria-label="Delete slide"
                                                data-testid={`slide-delete-${i}`}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="md:col-span-2">
                                            <label className={labelCls}>Eyebrow (small label above headline)</label>
                                            <input
                                                value={s.eyebrow}
                                                onChange={(e) => updateSlide(i, { eyebrow: e.target.value })}
                                                className={inputCls}
                                                data-testid={`slide-eyebrow-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Headline (top white line)</label>
                                            <input
                                                value={s.headline_top}
                                                onChange={(e) => updateSlide(i, { headline_top: e.target.value })}
                                                className={inputCls}
                                                data-testid={`slide-headline-top-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Headline accent (bottom gold line)</label>
                                            <input
                                                value={s.headline_accent || ""}
                                                onChange={(e) => updateSlide(i, { headline_accent: e.target.value })}
                                                className={inputCls}
                                                placeholder="Optional"
                                                data-testid={`slide-headline-accent-${i}`}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className={labelCls}>Subheading</label>
                                            <textarea
                                                rows={2}
                                                value={s.sub}
                                                onChange={(e) => updateSlide(i, { sub: e.target.value })}
                                                className={inputCls}
                                                data-testid={`slide-sub-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Primary CTA label</label>
                                            <input
                                                value={s.cta_label}
                                                onChange={(e) => updateSlide(i, { cta_label: e.target.value })}
                                                className={inputCls}
                                                data-testid={`slide-cta-label-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Primary CTA link</label>
                                            <input
                                                value={s.cta_href}
                                                onChange={(e) => updateSlide(i, { cta_href: e.target.value })}
                                                className={inputCls}
                                                placeholder="/shop"
                                                data-testid={`slide-cta-href-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Secondary CTA label (optional)</label>
                                            <input
                                                value={s.secondary_cta_label || ""}
                                                onChange={(e) => updateSlide(i, { secondary_cta_label: e.target.value || null })}
                                                className={inputCls}
                                                placeholder="Lab Standards"
                                                data-testid={`slide-secondary-cta-label-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Secondary CTA link (optional)</label>
                                            <input
                                                value={s.secondary_cta_href || ""}
                                                onChange={(e) => updateSlide(i, { secondary_cta_href: e.target.value || null })}
                                                className={inputCls}
                                                placeholder="/lab-standards"
                                                data-testid={`slide-secondary-cta-href-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Badge (optional)</label>
                                            <input
                                                value={s.badge || ""}
                                                onChange={(e) => updateSlide(i, { badge: e.target.value || null })}
                                                className={inputCls}
                                                placeholder="Limited Time"
                                                data-testid={`slide-badge-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Image URL (optional)</label>
                                            <input
                                                value={s.image || ""}
                                                onChange={(e) => updateSlide(i, { image: e.target.value || null })}
                                                className={inputCls}
                                                placeholder="/products/klow.png"
                                                data-testid={`slide-image-${i}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 flex justify-end">
                    <button onClick={saveSlides} disabled={saving} className="btn-solid" data-testid="slides-save-btn">
                        <Save size={14} /> Save slides
                    </button>
                </div>
            </section>

            {/* Live feed */}
            <section className="glass-elevated rounded-2xl p-6 md:p-8 border border-white/5">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div>
                        <h2 className="font-display text-xl font-bold text-white">Live Carbon Feed</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                            Slim animated ticker beneath the hero. Each message rotates with a Tiffany Blue accent icon.
                        </p>
                    </div>
                    <button
                        onClick={() => setFeed({ ...feed, active: !feed.active })}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${feed.active ? "border-[var(--tiffany)] text-[var(--tiffany)] bg-[var(--tiffany-soft)]" : "border-white/10 text-[var(--text-muted)]"}`}
                        data-testid="feed-active-toggle"
                    >
                        {feed.active ? <Eye size={12} /> : <EyeOff size={12} />}
                        {feed.active ? "Showing" : "Hidden"}
                    </button>
                </div>

                <div className="space-y-3" data-testid="feed-messages-list">
                    {feed.messages.map((m, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5" data-testid={`feed-row-${i}`}>
                            <GripVertical size={14} className="text-[var(--text-muted)] shrink-0" />
                            <select
                                value={m.icon}
                                onChange={(e) => updateMessage(i, { icon: e.target.value })}
                                className="px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[var(--tiffany)]"
                                data-testid={`feed-icon-${i}`}
                            >
                                {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                            <input
                                type="text"
                                value={m.text}
                                onChange={(e) => updateMessage(i, { text: e.target.value })}
                                className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[var(--tiffany)]"
                                placeholder="Orders dispatched daily..."
                                data-testid={`feed-text-${i}`}
                            />
                            <button
                                onClick={() => updateMessage(i, { active: !m.active })}
                                className={`p-2 rounded-lg border transition-colors ${m.active ? "border-[var(--tiffany)] text-[var(--tiffany)]" : "border-white/10 text-[var(--text-muted)]"}`}
                                aria-label="Toggle active"
                                data-testid={`feed-toggle-${i}`}
                            >
                                {m.active ? <Eye size={12} /> : <EyeOff size={12} />}
                            </button>
                            <button
                                onClick={() => removeMessage(i)}
                                className="p-2 rounded-lg border border-white/10 text-[var(--text-muted)] hover:text-red-400 hover:border-red-400/40"
                                aria-label="Delete"
                                data-testid={`feed-delete-${i}`}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
                    <button onClick={addMessage} className="btn-ghost" data-testid="feed-add-btn">
                        <Plus size={14} /> Add message
                    </button>
                    <button onClick={saveFeed} disabled={saving} className="btn-solid" data-testid="feed-save-btn">
                        <Save size={14} /> Save feed
                    </button>
                </div>
            </section>

            {/* Sections editor — Why Choose / How It Works / FAQ / Order / Visibility */}
            {sections && (
                <SectionsEditor
                    sections={sections}
                    setSections={setSections}
                    saving={saving}
                    onSave={saveSections}
                />
            )}
        </div>
    );
};

/* ─────────────────── Sections Editor (Why Choose / How It Works / FAQ) ─────────────────── */

const SECTION_LABELS = {
    announcement: "Announcement Banner",
    hero: "Hero Carousel",
    live_feed: "Live Feed Ticker",
    trust_bar: "Trust Bar",
    categories: "Category Cards",
    how_it_works: "How It Works",
    why_choose: "Why Choose Carbon Labs",
    reviews: "Customer Reviews",
    faq: "FAQ",
    community: "Carbon Community",
    newsletter: "Newsletter Signup",
};

const CARD_ICONS = ["flask", "truck", "lock", "headset", "award", "shield", "sparkles", "tag", "package", "grid", "beaker", "microscope", "brain", "activity", "layers", "search", "fileCheck"];

const SectionsEditor = ({ sections, setSections, saving, onSave }) => {
    const inputCls = "w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)] transition-colors";
    const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1";

    const moveSection = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= sections.order.length) return;
        const next = [...sections.order];
        [next[i], next[j]] = [next[j], next[i]];
        setSections({ ...sections, order: next });
    };

    const toggleSection = (key) => {
        setSections({ ...sections, visible: { ...sections.visible, [key]: !(sections.visible?.[key] ?? true) } });
    };

    const updateCard = (bucket, i, patch) => {
        const next = [...sections[bucket]];
        next[i] = { ...next[i], ...patch };
        setSections({ ...sections, [bucket]: next });
    };
    const moveCard = (bucket, i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= sections[bucket].length) return;
        const next = [...sections[bucket]];
        [next[i], next[j]] = [next[j], next[i]];
        // re-number steps if this is how_it_works
        if (bucket === "how_it_works") next.forEach((c, k) => { c.step = k + 1; });
        setSections({ ...sections, [bucket]: next });
    };
    const removeCard = (bucket, i) => {
        const next = sections[bucket].filter((_, idx) => idx !== i);
        if (bucket === "how_it_works") next.forEach((c, k) => { c.step = k + 1; });
        setSections({ ...sections, [bucket]: next });
    };
    const addCard = (bucket) => {
        const blank = bucket === "faq"
            ? { q: "New question", a: "Answer text", active: true }
            : bucket === "how_it_works"
                ? { step: (sections[bucket]?.length || 0) + 1, icon: "sparkles", title: "New step", desc: "Describe this step.", active: true }
                : { icon: "sparkles", title: "New feature", desc: "Describe this feature.", active: true };
        setSections({ ...sections, [bucket]: [...(sections[bucket] || []), blank] });
    };

    return (
        <>
            {/* Section order + visibility */}
            <section className="glass-elevated rounded-2xl p-6 md:p-8 border border-white/5" data-testid="sections-order-editor">
                <div className="flex items-start justify-between mb-5 gap-4">
                    <div>
                        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <ListOrdered size={16} className="text-[var(--gold)]" />
                            Homepage Section Order &amp; Visibility
                        </h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">Reorder or hide any homepage section without touching code.</p>
                    </div>
                    <button onClick={onSave} disabled={saving} className="btn-solid" data-testid="sections-save-btn">
                        <Save size={14} /> Save
                    </button>
                </div>
                <div className="space-y-2" data-testid="section-order-list">
                    {sections.order.map((key, i) => {
                        const on = sections.visible?.[key] ?? true;
                        return (
                            <div key={key} className={`flex items-center gap-3 rounded-xl p-3 border ${on ? "border-[rgba(233,188,11,0.2)] bg-black/30" : "border-white/5 bg-black/20 opacity-60"}`} data-testid={`section-row-${key}`}>
                                <div className="flex flex-col">
                                    <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-0.5 text-[var(--text-muted)] hover:text-white disabled:opacity-20" aria-label="Move up" data-testid={`section-up-${key}`}>
                                        <ChevronUp size={12} />
                                    </button>
                                    <button onClick={() => moveSection(i, 1)} disabled={i === sections.order.length - 1} className="p-0.5 text-[var(--text-muted)] hover:text-white disabled:opacity-20" aria-label="Move down" data-testid={`section-down-${key}`}>
                                        <ChevronDown size={12} />
                                    </button>
                                </div>
                                <GripVertical size={14} className="text-[var(--text-muted)]" />
                                <span className="flex-1 text-sm text-white font-mono">{SECTION_LABELS[key] || key}</span>
                                <button
                                    onClick={() => toggleSection(key)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${on ? "border-[var(--tiffany)] text-[var(--tiffany)] bg-[var(--tiffany-soft)]" : "border-white/10 text-[var(--text-muted)]"}`}
                                    data-testid={`section-toggle-${key}`}
                                >
                                    {on ? <Eye size={11} /> : <EyeOff size={11} />}
                                    {on ? "Visible" : "Hidden"}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Why Choose editor */}
            <CardBucketEditor
                title="Why Choose Carbon Labs"
                icon={<Sparkles size={16} className="text-[var(--gold)]" />}
                bucket="why_choose"
                items={sections.why_choose || []}
                iconOptions={CARD_ICONS}
                showStep={false}
                onUpdate={(i, patch) => updateCard("why_choose", i, patch)}
                onMove={(i, dir) => moveCard("why_choose", i, dir)}
                onRemove={(i) => removeCard("why_choose", i)}
                onAdd={() => addCard("why_choose")}
                inputCls={inputCls}
                labelCls={labelCls}
            />

            {/* How It Works editor */}
            <CardBucketEditor
                title="How It Works"
                icon={<ListOrdered size={16} className="text-[var(--gold)]" />}
                bucket="how_it_works"
                items={sections.how_it_works || []}
                iconOptions={CARD_ICONS}
                showStep={true}
                onUpdate={(i, patch) => updateCard("how_it_works", i, patch)}
                onMove={(i, dir) => moveCard("how_it_works", i, dir)}
                onRemove={(i) => removeCard("how_it_works", i)}
                onAdd={() => addCard("how_it_works")}
                inputCls={inputCls}
                labelCls={labelCls}
            />

            {/* FAQ editor */}
            <section className="glass-elevated rounded-2xl p-6 md:p-8 border border-white/5" data-testid="faq-editor">
                <div className="flex items-start justify-between mb-5 gap-4">
                    <div>
                        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <HelpCircle size={16} className="text-[var(--gold)]" />
                            FAQ
                        </h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">Homepage accordion. Full FAQ page is separate.</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => addCard("faq")} className="btn-ghost" data-testid="faq-add-btn">
                            <Plus size={14} /> Add
                        </button>
                        <button onClick={onSave} disabled={saving} className="btn-solid" data-testid="faq-save-btn">
                            <Save size={14} /> Save
                        </button>
                    </div>
                </div>
                <div className="space-y-3">
                    {(sections.faq || []).map((f, i) => (
                        <div key={i} className={`rounded-xl p-4 border ${f.active !== false ? "border-[rgba(233,188,11,0.2)] bg-black/30" : "border-white/5 bg-black/20 opacity-60"}`} data-testid={`faq-row-${i}`}>
                            <div className="flex items-start gap-3">
                                <div className="flex flex-col">
                                    <button onClick={() => moveCard("faq", i, -1)} disabled={i === 0} className="p-1 text-[var(--text-muted)] hover:text-white disabled:opacity-20" data-testid={`faq-up-${i}`}><ChevronUp size={12} /></button>
                                    <button onClick={() => moveCard("faq", i, 1)} disabled={i === sections.faq.length - 1} className="p-1 text-[var(--text-muted)] hover:text-white disabled:opacity-20" data-testid={`faq-down-${i}`}><ChevronDown size={12} /></button>
                                </div>
                                <div className="flex-1 min-w-0 space-y-2">
                                    <input value={f.q} onChange={(e) => updateCard("faq", i, { q: e.target.value })} className={inputCls} placeholder="Question" data-testid={`faq-q-${i}`} />
                                    <textarea value={f.a} onChange={(e) => updateCard("faq", i, { a: e.target.value })} rows={2} className={`${inputCls} resize-y`} placeholder="Answer" data-testid={`faq-a-${i}`} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button onClick={() => updateCard("faq", i, { active: !(f.active !== false) })} className={`p-2 rounded-lg border ${f.active !== false ? "border-[var(--tiffany)] text-[var(--tiffany)]" : "border-white/10 text-[var(--text-muted)]"}`} data-testid={`faq-toggle-${i}`}>
                                        {f.active !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                                    </button>
                                    <button onClick={() => removeCard("faq", i)} className="p-2 rounded-lg border border-white/10 text-[var(--text-muted)] hover:text-red-400 hover:border-red-400/40" data-testid={`faq-delete-${i}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
};

const CardBucketEditor = ({ title, icon, bucket, items, iconOptions, showStep, onUpdate, onMove, onRemove, onAdd, inputCls, labelCls }) => (
    <section className="glass-elevated rounded-2xl p-6 md:p-8 border border-white/5" data-testid={`${bucket}-editor`}>
        <div className="flex items-start justify-between mb-5 gap-4">
            <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                {icon} {title}
            </h2>
            <button onClick={onAdd} className="btn-ghost" data-testid={`${bucket}-add-btn`}>
                <Plus size={14} /> Add card
            </button>
        </div>
        <div className="space-y-3">
            {items.map((c, i) => (
                <div key={i} className={`rounded-xl p-4 border ${c.active !== false ? "border-[rgba(233,188,11,0.2)] bg-black/30" : "border-white/5 bg-black/20 opacity-60"}`} data-testid={`${bucket}-row-${i}`}>
                    <div className="flex items-start gap-3">
                        <div className="flex flex-col">
                            <button onClick={() => onMove(i, -1)} disabled={i === 0} className="p-1 text-[var(--text-muted)] hover:text-white disabled:opacity-20" data-testid={`${bucket}-up-${i}`}><ChevronUp size={12} /></button>
                            <button onClick={() => onMove(i, 1)} disabled={i === items.length - 1} className="p-1 text-[var(--text-muted)] hover:text-white disabled:opacity-20" data-testid={`${bucket}-down-${i}`}><ChevronDown size={12} /></button>
                        </div>
                        <div className="flex-1 min-w-0 grid md:grid-cols-3 gap-2">
                            {showStep && (
                                <div>
                                    <label className={labelCls}>Step #</label>
                                    <input type="number" min={1} value={c.step ?? (i + 1)} onChange={(e) => onUpdate(i, { step: Number(e.target.value) })} className={inputCls} data-testid={`${bucket}-step-${i}`} />
                                </div>
                            )}
                            <div>
                                <label className={labelCls}>Icon</label>
                                <select value={c.icon || "sparkles"} onChange={(e) => onUpdate(i, { icon: e.target.value })} className={inputCls} data-testid={`${bucket}-icon-${i}`}>
                                    {iconOptions.map((k) => <option key={k} value={k}>{k}</option>)}
                                </select>
                            </div>
                            <div className={showStep ? "" : "md:col-span-2"}>
                                <label className={labelCls}>Title</label>
                                <input value={c.title} onChange={(e) => onUpdate(i, { title: e.target.value })} className={inputCls} data-testid={`${bucket}-title-${i}`} />
                            </div>
                            <div className="md:col-span-3">
                                <label className={labelCls}>Description</label>
                                <textarea value={c.desc} onChange={(e) => onUpdate(i, { desc: e.target.value })} rows={2} className={`${inputCls} resize-y`} data-testid={`${bucket}-desc-${i}`} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => onUpdate(i, { active: !(c.active !== false) })} className={`p-2 rounded-lg border ${c.active !== false ? "border-[var(--tiffany)] text-[var(--tiffany)]" : "border-white/10 text-[var(--text-muted)]"}`} data-testid={`${bucket}-toggle-${i}`}>
                                {c.active !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                            </button>
                            <button onClick={() => onRemove(i)} className="p-2 rounded-lg border border-white/10 text-[var(--text-muted)] hover:text-red-400 hover:border-red-400/40" data-testid={`${bucket}-delete-${i}`}>
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);
